import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import SpeakerIcon from '../../svgs/SpeakerIcon';
import SpeakerMutedIcon from '../../svgs/SpeakerMutedIcon';
import { COMPACT_TOOLTIP_STYLE } from './tooltipStyles';

import type { PlayerVolumeControls } from '@src/hooks/usePlayerVolume';

type Props = PlayerVolumeControls & {
  disabled?: boolean;
  /** Slider rail width in px (default 72). A narrow dock passes less. */
  sliderWidth?: number;
};

/** How long the pointer may leave the control before the slider collapses. */
const CLOSE_GRACE_MS = 200;
const KEYBOARD_STEP = 0.05;

/**
 * Speaker button + inline horizontal volume slider shared by the player
 * transport bars (Creator Studio stage, Content Studio clip preview).
 * Clicking the speaker mutes/unmutes; hovering (or focusing) it expands the
 * slider beside the button. The slider lives inside the bar row, so no
 * portal or z-index handling is needed. Purely presentational — state and
 * renderer plumbing live in `usePlayerVolume`.
 */
export const PlayerVolumeControl = ({
  volume,
  muted,
  setVolume,
  toggleMute,
  disabled = false,
  sliderWidth = 72,
}: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    cancelCloseTimer();
    if (!disabled) setOpen(true);
  }, [cancelCloseTimer, disabled]);

  const scheduleClose = useCallback(() => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      if (!draggingRef.current) setOpen(false);
    }, CLOSE_GRACE_MS);
  }, [cancelCloseTimer]);

  const closeNow = useCallback(() => {
    cancelCloseTimer();
    if (!draggingRef.current) setOpen(false);
  }, [cancelCloseTimer]);

  useOutsideAlerter(wrapRef, closeNow);

  // Escape collapses the slider without bubbling on to modal-level handlers.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closeNow();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, closeNow]);

  useEffect(
    () => () => {
      cancelCloseTimer();
      dragCleanupRef.current?.();
    },
    [cancelCloseTimer],
  );

  const applyFromClientX = useCallback(
    (clientX: number, commit: boolean) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const pct = (clientX - rect.left) / rect.width;
      const next = Math.min(1, Math.max(0, pct));
      setVolume(next, commit ? { commit: true } : undefined);
    },
    [setVolume],
  );

  const onTrackMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setDragging(true);
      draggingRef.current = true;
      applyFromClientX(event.clientX, false);

      const onMove = (ev: MouseEvent) => applyFromClientX(ev.clientX, false);
      const onUp = (ev: MouseEvent) => {
        cleanup();
        setDragging(false);
        draggingRef.current = false;
        applyFromClientX(ev.clientX, true);
        if (
          wrapRef.current &&
          ev.target instanceof Node &&
          !wrapRef.current.contains(ev.target)
        ) {
          setOpen(false);
        }
      };
      const cleanup = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        dragCleanupRef.current = null;
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      dragCleanupRef.current = cleanup;
    },
    [applyFromClientX],
  );

  const onTrackKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const up = event.key === 'ArrowUp' || event.key === 'ArrowRight';
      const down = event.key === 'ArrowDown' || event.key === 'ArrowLeft';
      if (!up && !down) return;
      event.preventDefault();
      event.stopPropagation();
      const current = muted ? 0 : volume;
      setVolume(current + (up ? KEYBOARD_STEP : -KEYBOARD_STEP), {
        commit: true,
      });
    },
    [muted, volume, setVolume],
  );

  const displayPct = Math.round((muted ? 0 : volume) * 100);
  const showSlider = (open || dragging) && !disabled;

  return (
    <Wrap
      ref={wrapRef}
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      data-testid="player-volume-control"
    >
      <SpeakerButton
        type="button"
        onClick={toggleMute}
        onFocus={openNow}
        disabled={disabled}
        $active={showSlider}
        aria-label={muted ? 'Unmute' : 'Mute'}
        aria-pressed={muted}
        data-tooltip-id="player-volume-tooltip"
        data-tooltip-content={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? (
          <SpeakerMutedIcon
            width="18"
            height="18"
            fillColor="var(--foreground)"
            strokeColor="var(--foreground)"
          />
        ) : (
          <SpeakerIcon
            width="18"
            height="18"
            fillColor="var(--foreground)"
            strokeColor="var(--foreground)"
          />
        )}
      </SpeakerButton>
      {/* The slot is always laid out at the slider's width, so opening the
          slider never widens the transport row (which fed back into the
          stage's width and re-fit the canvas on every hover). */}
      <SliderSlot style={{ width: sliderWidth }}>
        {showSlider && (
          <SliderTrack
            ref={trackRef}
            data-testid="player-volume-slider"
            role="slider"
            aria-label="Volume"
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={displayPct}
            tabIndex={0}
            onMouseDown={onTrackMouseDown}
            onKeyDown={onTrackKeyDown}
          >
            <SliderRail>
              <SliderFill style={{ width: `${displayPct}%` }} />
            </SliderRail>
          </SliderTrack>
        )}
      </SliderSlot>
      {ReactDOM.createPortal(
        <Tooltip
          id="player-volume-tooltip"
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </Wrap>
  );
};

export default PlayerVolumeControl;

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SpeakerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) =>
    $active ? 'var(--surface-elevated)' : 'transparent'};
  border: none;
  padding: 6px;
  margin: 0;
  cursor: pointer;
  color: var(--foreground);
  border-radius: var(--radius-button, 6px);
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  transition:
    opacity 0.15s ease,
    background 0.15s ease;

  &:hover {
    opacity: 1;
    background: var(--surface-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    background: transparent;
  }
`;

/** Reserved footprint for the slider; empty while it is collapsed. */
const SliderSlot = styled.div`
  flex-shrink: 0;
  height: 20px;
  display: flex;
  align-items: center;
`;

/** Full-height hit area around the thin rail so the slider is easy to grab. */
const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const SliderRail = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: var(--text-muted);
  overflow: hidden;
`;

const SliderFill = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 2px;
  /* Gold — the design system's color for visual/creative controls
     (green is reserved for action buttons). */
  background: var(--accent);
`;
